import { AutomationTrigger } from './AutomationTrigger.js';
import { AutomationAction } from './AutomationAction.js';
import { AutomationCondition } from './AutomationCondition.js';
import { AutomationBinding } from './AutomationBinding.js';

/**
 * AutomationModel
 *
 * Root of the Automation AST. Describes a complete automation rule:
 *
 *   WHEN   triggers fire
 *   IF     conditions hold
 *   THEN   execute actions
 *
 * …or a set of continuous state bindings:
 *
 *   BIND   property ← strategy   (bindings[])
 *
 * This is the ONLY persisted automation model.
 * It does not reference any specific editor UI or Home Assistant.
 *
 * The Automation Editor reads and writes AutomationModel instances directly.
 * Generators consume compiled AutomationModel instances to produce
 * target output (HA YAML, ESPHome, Node-RED, …).
 *
 * _providerExtra carries opaque provider-specific metadata that cannot
 * be expressed in the generic model. Generators that need it (e.g. the
 * HA Generator for openhasp.command popup JSONL) read it; all other
 * generators ignore it.
 */
export class AutomationModel {
  /**
   * @param {object} config
   * @param {string} [config.id]
   * @param {number|null} [config.widgetPageId]   — openHASP page number
   * @param {number|null} [config.widgetHaspId]   — openHASP object ID
   * @param {string|null} [config.widgetType]     — LVGL object type (e.g. 'btn', 'switch')
   * @param {AutomationTrigger[]} [config.triggers]
   * @param {AutomationAction[]} [config.actions]
   * @param {AutomationCondition[]} [config.conditions]
   * @param {AutomationBinding[]} [config.bindings]  — continuous state bindings
   * @param {object|null} [config._providerExtra] — opaque provider metadata
   */
  constructor({
    id,
    widgetPageId = null,
    widgetHaspId = null,
    widgetType   = null,
    triggers     = [],
    actions      = [],
    conditions   = [],
    bindings     = [],
    _providerExtra = null,
  } = {}) {
    this.id           = id ?? crypto.randomUUID();
    this.widgetPageId = widgetPageId;
    this.widgetHaspId = widgetHaspId;
    this.widgetType   = widgetType;

    this.triggers = triggers.map(t =>
      t instanceof AutomationTrigger ? t : AutomationTrigger.fromJSON(t)
    );
    this.actions = actions.map(a =>
      a instanceof AutomationAction ? a : AutomationAction.fromJSON(a)
    );
    this.conditions = conditions.map(c =>
      c instanceof AutomationCondition ? c : AutomationCondition.fromJSON(c)
    );
    this.bindings = bindings.map(b =>
      b instanceof AutomationBinding ? b : AutomationBinding.fromJSON(b)
    );

    this._providerExtra = _providerExtra ?? null;
  }

  toJSON() {
    const out = {
      id:           this.id,
      widgetPageId: this.widgetPageId,
      widgetHaspId: this.widgetHaspId,
      widgetType:   this.widgetType,
      triggers:     this.triggers.map(t => t.toJSON()),
      actions:      this.actions.map(a => a.toJSON()),
      conditions:   this.conditions.map(c => c.toJSON()),
    };
    if (this.bindings.length > 0) out.bindings = this.bindings.map(b => b.toJSON());
    if (this._providerExtra !== null) out._providerExtra = this._providerExtra;
    return out;
  }

  static fromJSON(data) {
    if (!data) return null;
    return new AutomationModel(data);
  }
}
